/**
 * Shipping insurance — keeps a tier-priced insurance line in sync with the cart.
 *
 * Config comes from snippets/shipping-insurance-config.liquid, UI from
 * snippets/shipping-insurance.liquid.
 *
 * Rendering is delegated to Skivys' <cart-drawer>: after a successful insurance
 * mutation the module hands the fresh cart to drawer.renderFromExternal(cart,
 * SOURCE), and the drawer re-renders itself through its own Section Rendering API
 * fetch exactly as it does for every other cart mutation. The SOURCE tag is what
 * stops the drawer's own `cart-drawer:updated` event from bouncing back in here
 * and starting another sync.
 *
 * Exposes:
 *   window.ShippingInsurance  = { sync(immediate), flush() }
 *   window.CartDrawerFilters  = { isInsuranceItem, merchandiseCount,
 *                                 merchandiseSubtotalCents, cartAppearsEmpty }
 */
(function () {
  'use strict';

  const SOURCE = 'shipping-insurance';
  const ON_CHANGE_DEBOUNCE_TIMER = 300;
  const CHECKOUT_FLUSH_TIMEOUT = 2500;
  // Skivys' drawer checkout control is a plain <a href="/checkout">, not a form
  // submit button. The other two selectors cover the cart page / any theme form.
  const CHECKOUT_SELECTOR = 'a.cart-drawer__checkout-btn, button[name="checkout"], #checkout';
  const CHECKOUT_LOADING_CLASS = 'shipping-insurance-btn-loading';

  let syncInProgress = false;
  let pendingSync = false;
  let debounceTimer = null;
  let debounceScheduled = false;
  let currentSync = Promise.resolve();
  let toggleDelegationBound = false;
  let checkoutSubmitting = false;

  /* ------------------------------------------------------------------ config */

  // Read on every call: shipping-insurance-config.liquid may be parsed after this file.
  function getConfig() {
    const config = window.shippingInsuranceConfig;
    return config && config.enabled ? config : null;
  }

  /* ------------------------------------------------------------------ routes */

  /**
   * Skivys has no route registry, so the cart endpoints are hardcoded behind the
   * locale/market root path Shopify publishes. The root already ends in a slash
   * on every storefront that sets it, but it is normalised here so a root without
   * one cannot produce `/encart.js` and one with a trailing slash cannot produce
   * `//cart.js`.
   */
  function rootPath() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function cartUrl() {
    return rootPath() + 'cart.js';
  }

  function cartAddUrl() {
    return rootPath() + 'cart/add.js';
  }

  function cartChangeUrl() {
    return rootPath() + 'cart/change.js';
  }

  function cartUpdateUrl() {
    return rootPath() + 'cart/update.js';
  }

  function fetchConfig() {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
  }

  /* -------------------------------------------------------------- cart state */

  // Cart JSON carries no product tags, so lines are matched by property, variant
  // or product only. The tag check lives on the Liquid side.
  function isInsuranceItem(item, config) {
    if (!item) return false;

    if (item.properties && item.properties[config.propertyKey]) return true;
    if ((config.variantIds || []).indexOf(item.id) !== -1) return true;

    return config.productId > 0 && item.product_id === config.productId;
  }

  /**
   * /cart/add.js returns only the items it added and has no `attributes` key, so
   * it is not a cart. Treating it as one makes the toggle uncheck itself after
   * add-to-cart, because the missing attribute reads as "opted out".
   */
  function isUsableCartState(state) {
    return !!state && Array.isArray(state.items) && 'attributes' in state;
  }

  async function fetchCart() {
    const response = await fetch(cartUrl(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    return response.json();
  }

  async function postCart(url, body) {
    const response = await fetch(url, { ...fetchConfig(), body: JSON.stringify(body) });
    const parsed = await response.json();

    if (parsed && parsed.status && Number(parsed.status) >= 400) {
      throw new Error(parsed.description || parsed.message || 'Cart request failed');
    }
    return parsed;
  }

  function summarize(cart, config) {
    const lines = [];
    let merchandiseCount = 0;
    let merchandiseSubtotalCents = 0;
    let insuranceSubtotalCents = 0;

    (cart.items || []).forEach((item) => {
      if (isInsuranceItem(item, config)) {
        lines.push(item);
        insuranceSubtotalCents += item.final_line_price;
      } else {
        merchandiseCount += item.quantity;
        merchandiseSubtotalCents += item.final_line_price;
      }
    });

    return {
      lines: lines,
      merchandiseCount: merchandiseCount,
      merchandiseSubtotalCents: merchandiseSubtotalCents,
      insuranceSubtotalCents: insuranceSubtotalCents,
      optedOut: (cart.attributes || {})[config.optOutAttribute] === 'true',
    };
  }

  /** Highest configured tier whose minimum the basis reaches. */
  function resolveTier(config, basisCents) {
    let match = null;

    (config.tiers || []).forEach((tier) => {
      if (tier.minCents <= basisCents && (match === null || tier.minCents >= match.minCents)) {
        match = tier;
      }
    });

    return match;
  }

  /**
   * Cheapest variant priced at or above `priceCents`, falling back to the dearest
   * one when the target overshoots the ladder — a cart beyond the top variant is
   * charged that ceiling rather than losing its cover entirely.
   */
  function resolveVariantForPrice(config, priceCents) {
    let above = null;
    let dearest = null;

    (config.priceVariants || []).forEach((pair) => {
      if (pair[0] >= priceCents && (above === null || pair[0] < above[0])) above = pair;
      if (dearest === null || pair[0] > dearest[0]) dearest = pair;
    });

    const chosen = above || dearest;
    return chosen ? chosen[1] : null;
  }

  /**
   * A percentage tier has no variant of its own: the fee is a percentage of the
   * basis, rounded to a whole number of ladder steps so it lands on a real variant
   * price. Keep in sync with the display copy in snippets/shipping-insurance.liquid.
   */
  function resolveTierVariantId(config, basisCents) {
    const tier = resolveTier(config, basisCents);
    if (!tier) return null;

    if (tier.percent > 0) {
      const step = config.percentStepCents > 0 ? config.percentStepCents : 10;
      return resolveVariantForPrice(config, Math.round((basisCents * tier.percent) / 100 / step) * step);
    }

    return tier.variantId || null;
  }

  /* ----------------------------------------------------------------- theme IO */

  /**
   * Hand the fresh cart to the drawer so it re-renders through its own section
   * fetch, tagged as ours so its `cart-drawer:updated` event is ignored below.
   * Safe when no drawer exists — e.g. on /cart, or before the custom element has
   * upgraded — in which case the mutation simply lands without a re-render.
   */
  async function renderDrawer(cart) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderFromExternal === 'function') {
      await drawer.renderFromExternal(cart, SOURCE);
    }
  }

  /* ---------------------------------------------------------------------- UI */

  function getWidgets() {
    return document.querySelectorAll('[data-shipping-insurance]');
  }

  function setSyncingUI(isSyncing) {
    document.body.toggleAttribute('data-insurance-syncing', isSyncing);

    getWidgets().forEach((widget) => {
      widget.setAttribute('data-syncing', isSyncing ? 'true' : 'false');
      widget.setAttribute('aria-busy', isSyncing ? 'true' : 'false');

      const toggle = widget.querySelector('[data-insurance-toggle]');
      if (toggle) toggle.disabled = isSyncing;
    });
  }

  function showError(message) {
    getWidgets().forEach((widget) => {
      const target = widget.querySelector('[data-insurance-error]');
      if (target) target.textContent = message || '';
    });
  }

  /**
   * Drives the toggle and the two-state copy together. The state attribute is what
   * CSS keys the icon/title/subtitle variants off, so flipping it here swaps them
   * immediately — the shopper does not wait on the cart round-trip — and every
   * caller (optimistic flip, failed-request revert, post-render reconcile) stays
   * in step. The label follows the visible title, so the switch is never announced
   * with copy that is no longer on screen.
   */
  function setToggleState(checked) {
    const state = checked ? 'protected' : 'unprotected';

    getWidgets().forEach((widget) => {
      widget.setAttribute('data-insurance-state', state);

      const toggle = widget.querySelector('[data-insurance-toggle]');
      if (!toggle) return;

      toggle.checked = checked;
      toggle.setAttribute('aria-checked', checked ? 'true' : 'false');

      const label = widget.getAttribute(`data-label-${state}`);
      if (label) toggle.setAttribute('aria-label', label);
    });
  }

  /** Re-point the toggle at the cart attribute after the DOM is replaced. */
  async function reconcileToggles(state) {
    const config = getConfig();
    if (!config) return;

    const cart = isUsableCartState(state) ? state : await fetchCart();
    setToggleState((cart.attributes || {})[config.optOutAttribute] !== 'true');
  }

  /* -------------------------------------------------------------------- sync */

  /**
   * Sets a cart line's quantity, addressed by its line item key rather than its
   * position.
   *
   * A line number is only meaningful for the exact cart it was read from. /cart/add
   * .js appends, so it does not disturb lower numbers on its own — but any other
   * mutation landing in between (the shopper removing an item, a second tab, an
   * app) renumbers the lines, and the change then hits whichever line now sits in
   * that slot instead of the one it was aiming at.
   *
   * Keys are stable across all of that.
   */
  function setLineQuantity(item, quantity) {
    return postCart(cartChangeUrl(), { id: item.key, quantity: quantity });
  }

  /** Drops a cart line. Keyed, via setLineQuantity. */
  function removeLine(item) {
    return setLineQuantity(item, 0);
  }

  async function runSync() {
    const config = getConfig();
    if (!config) return;

    let cart = await fetchCart();
    let state = summarize(cart, config);

    // Collapse duplicates before anything else, then re-read the cart and fix
    // whatever survived. Keyed removal makes the order irrelevant.
    if (state.lines.length > 1) {
      for (let i = 1; i < state.lines.length; i++) {
        await removeLine(state.lines[i]);
      }
      cart = await fetchCart();
      state = summarize(cart, config);
    }

    const basisCents =
      config.subtotalBasis === 'total_price'
        ? cart.total_price - state.insuranceSubtotalCents
        : state.merchandiseSubtotalCents;

    const targetVariantId = resolveTierVariantId(config, basisCents);
    const wantsInsurance = state.merchandiseCount > 0 && !state.optedOut && !!targetVariantId;
    const existing = state.lines[0] || null;

    if (!wantsInsurance) {
      if (!existing) return cart;

      const updated = await removeLine(existing);
      await renderDrawer(updated);
      return updated;
    }

    if (existing && String(existing.id) === String(targetVariantId)) {
      // The line is a fee, so it is always exactly one unit — never a quantity the
      // shopper gets to pick. Two live paths produce more: the insurance product is
      // published, so its own /products/… add-to-cart form works, and /cart is
      // mutable through /cart/change.js (and through a stale tab) even with the row
      // hidden. Comparing only the variant let those stand forever, and the widget
      // agreed with them because it prints the whole line total.
      //
      // The variant is already right, so only the quantity moves; the next sync
      // then reads quantity 1 and returns here instead.
      if (Number(existing.quantity) === 1) return cart;

      const updated = await setLineQuantity(existing, 1);
      await renderDrawer(updated);
      return updated;
    }

    const addBody = {
      items: [
        {
          id: Number(targetVariantId),
          quantity: 1,
          properties: { [config.propertyKey]: 'true' },
        },
      ],
    };

    if (!existing) {
      await postCart(cartAddUrl(), addBody);

      // /cart/add.js returns only the added items, so re-read the cart before
      // handing it to the drawer.
      const cartJson = await fetchCart();
      await renderDrawer(cartJson);
      return cartJson;
    }

    // Tier change. Shopify's cart cannot repoint a line at another variant, so the
    // new tier is added and the old line removed by key. Only the second request's
    // result is rendered so the shopper sees a single re-render, and because the
    // line is hidden its move to the end of the cart is invisible.
    await postCart(cartAddUrl(), addBody);
    const updated = await removeLine(existing);
    await renderDrawer(updated);
    return updated;
  }

  async function processSync() {
    // syncInProgress must be cleared in a finally — a throw that leaves it set
    // would wedge every future sync for the life of the page.
    syncInProgress = true;
    setSyncingUI(true);

    let cart = null;

    try {
      cart = await runSync();
      showError('');
    } catch (error) {
      // Surfaced, not just logged: a swallowed failure here leaves the widget
      // advertising a fee the cart does not hold, which reads as the price simply
      // refusing to move.
      console.error('Shipping insurance sync failed:', error);
      const config = getConfig();
      if (config) showError(config.strings.error);
    } finally {
      syncInProgress = false;
      setSyncingUI(false);
    }

    if (pendingSync) {
      pendingSync = false;
      return processSync();
    }

    // Rendering above replaced the widget markup, so re-point the toggle at the
    // cart attribute. Reuses the cart we already hold rather than refetching.
    await reconcileToggles(cart);
  }

  function startSync() {
    if (syncInProgress) {
      pendingSync = true;
      return currentSync;
    }

    currentSync = processSync();
    return currentSync;
  }

  function scheduleSync(immediate) {
    if (!getConfig()) return currentSync;

    clearTimeout(debounceTimer);

    if (immediate) {
      debounceScheduled = false;
      return startSync();
    }

    debounceScheduled = true;
    debounceTimer = setTimeout(function () {
      debounceScheduled = false;
      startSync();
    }, ON_CHANGE_DEBOUNCE_TIMER);

    return currentSync;
  }

  /** Settle any outstanding work. A debounced sync is run now, not discarded. */
  function flush() {
    if (!getConfig()) return Promise.resolve();

    if (debounceScheduled) {
      clearTimeout(debounceTimer);
      debounceScheduled = false;
      return startSync();
    }

    return currentSync;
  }

  /* ------------------------------------------------------------------ toggle */

  async function onToggleChange(event) {
    const toggle = event.target.closest('[data-insurance-toggle]');
    if (!toggle) return;

    const config = getConfig();
    if (!config) return;

    const previous = !toggle.checked;
    const optOut = !toggle.checked;

    // Swaps the copy and icon in the same frame as the knob; reverted below if the
    // cart request fails, so the widget never claims cover the cart does not hold.
    setToggleState(toggle.checked);
    showError('');
    setSyncingUI(true);

    try {
      await postCart(cartUpdateUrl(), {
        attributes: { [config.optOutAttribute]: optOut ? 'true' : '' },
      });
      await scheduleSync(true);
    } catch (error) {
      console.error('Shipping insurance toggle failed:', error);
      setToggleState(previous);
      showError(config.strings.error);
    } finally {
      setSyncingUI(false);
    }
  }

  /**
   * One delegated listener, because the drawer replaces its body/footer innerHTML
   * on every cart mutation. A listener bound to the input itself is orphaned by
   * that swap — the toggle then still flips visually via CSS :checked but fires no
   * cart logic at all.
   */
  function bindToggleDelegation() {
    if (toggleDelegationBound) return;

    document.addEventListener('change', onToggleChange);
    toggleDelegationBound = true;
  }

  /* ---------------------------------------------------------------- checkout */

  function timeoutPromise(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Skivys' drawer checkout control is `<a href="/checkout">`, so there is no form
   * to submit — the click is intercepted, the pending insurance work is flushed,
   * and the browser is then navigated by hand. `button[name="checkout"]` / #checkout
   * keep working for any real form-backed checkout button elsewhere in the theme.
   *
   * Capture phase, so this runs before the drawer's own document-level click
   * handler.
   */
  function bindCheckoutInterception() {
    document.addEventListener(
      'click',
      function (event) {
        const checkoutEl = event.target.closest(CHECKOUT_SELECTOR);
        if (!checkoutEl || !getConfig()) return;

        // Second click while the first is still flushing: swallow it rather than
        // letting it start a competing navigation.
        if (checkoutSubmitting) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        const form = checkoutEl.form || null;

        // A form-backed button can sit in an invalid form — the cart page's quantity
        // inputs carry min/max/step. Bail out and let the browser report it
        // natively; intercepting would swallow the click and leave a dead button.
        if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) return;

        event.preventDefault();
        event.stopPropagation();
        checkoutSubmitting = true;
        checkoutEl.classList.add(CHECKOUT_LOADING_CLASS);

        // A stalled cart request must never block checkout, so the flush races a
        // hard timeout and the shopper is sent to checkout either way.
        Promise.race([
          Promise.resolve(flush()).catch(function () {}),
          timeoutPromise(CHECKOUT_FLUSH_TIMEOUT),
        ]).then(function () {
          if (checkoutEl.tagName === 'A') {
            window.location.href = checkoutEl.getAttribute('href') || '/checkout';
            return;
          }

          if (form && typeof form.requestSubmit === 'function') {
            // Submit with the button as submitter so its name="checkout" is in the
            // form entry list. Disabling it first would drop the submitter and post
            // to /cart instead of /checkout — hence checkoutSubmitting above rather
            // than pre-disabling.
            form.requestSubmit(checkoutEl);
          } else if (form) {
            form.submit();
          } else {
            window.location.href = '/checkout';
          }
        });
      },
      true
    );
  }

  /* ----------------------------------------------------------- cart subscribe */

  /**
   * Skivys has no theme-level event bus, so the drawer's plain DOM event stands
   * in: it dispatches `cart-drawer:updated` with { cart, source } after every
   * render, and mutations tagged with our SOURCE are skipped so we do not loop.
   */
  function bindCartUpdateSubscription() {
    document.addEventListener('cart-drawer:updated', function (event) {
      const detail = (event && event.detail) || {};
      if (detail.source === SOURCE) return; // our own mutation — ignore or we loop

      reconcileToggles(detail.cart);
      scheduleSync(false);
    });
  }

  /* -------------------------------------------------------------------- init */

  function init() {
    const config = getConfig();
    if (!config) return;

    bindToggleDelegation();
    bindCheckoutInterception();
    bindCartUpdateSubscription();

    // Any non-empty cart syncs on load. Keyed off the raw Shopify count rather
    // than the widget's presence, so a cart left holding only a stale insurance
    // line — which renders as empty, widget included — still gets cleaned up.
    if (config.cartItemCount > 0) scheduleSync(true);
  }

  window.ShippingInsurance = {
    sync: scheduleSync,
    flush: flush,
  };

  /**
   * Helpers for hiding the shipping insurance line from cart counts and totals.
   *
   * The insurance line is a real product line item, so every surface that counts
   * items or sums money has to skip it. Config is read on each call rather than
   * captured once — shipping-insurance-config.liquid may be parsed after this file.
   *
   * Cart JSON line items carry no product tags, so lines are matched by property
   * key, variant ID or product ID only (the tag is used on the Liquid side).
   *
   * Defined unconditionally, outside init(), so assets/cart-drawer.js can call it
   * even when insurance is switched off. With no config, or insurance disabled,
   * every helper returns the untouched cart value so behaviour is identical to
   * having no insurance feature at all.
   */
  window.CartDrawerFilters = {
    get config() {
      const config = window.shippingInsuranceConfig;
      return config && config.enabled ? config : null;
    },

    isInsuranceItem(item) {
      const config = window.CartDrawerFilters.config;
      if (!config || !item) return false;

      return isInsuranceItem(item, config);
    },

    /** cart.item_count without insurance units. */
    merchandiseCount(cart) {
      if (!cart) return 0;
      if (!window.CartDrawerFilters.config || !Array.isArray(cart.items)) return cart.item_count;

      return cart.items.reduce((count, item) => {
        return window.CartDrawerFilters.isInsuranceItem(item) ? count : count + item.quantity;
      }, 0);
    },

    /** cart.items_subtotal_price without the insurance line. */
    merchandiseSubtotalCents(cart) {
      if (!cart) return 0;
      if (!window.CartDrawerFilters.config || !Array.isArray(cart.items)) return cart.items_subtotal_price;

      return cart.items.reduce((total, item) => {
        return window.CartDrawerFilters.isInsuranceItem(item) ? total : total + item.final_line_price;
      }, 0);
    },

    /** A cart holding nothing but insurance has to read as empty. */
    cartAppearsEmpty(cart) {
      return window.CartDrawerFilters.merchandiseCount(cart) === 0;
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
